import React, { useState, useRef, useEffect } from 'react'
import {
  Box,
  Text,
  Input,
  Flex,
  FormControl,
  FormLabel,
  Textarea,
  Select,
  Switch,
  useToast,
  InputGroup,
  IconButton,
  Image,
  SimpleGrid,
  FormErrorMessage,
} from '@chakra-ui/react'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { FiUpload, FiX } from 'react-icons/fi'
import { useGenerateImage } from '../../../hooks/useGenerateImage'
import { SliderWithTooltip } from '../../../components/SliderWithTooltip'
import { ActionButton } from '../../../components/ActionButton'
import { usePatternContext } from '../../../context/PatternContext'
import { useQuery } from 'react-query'
import { fetchProducts, getAspectRatio } from '../../../services/api'
import { useNavigate } from 'react-router-dom'

const formSchema = z.object({
  produto: z.string().nonempty('Selecione um produto'),
  prompt: z.string().min(10, 'Descrição muito curta (mín. 10 caracteres)').nonempty('Este campo é obrigatório'),
  categoria: z.string().nonempty('Selecione uma categoria'),
  tema: z.string().nonempty('Informe o tema da linha'),
  estiloArtistico: z.number().min(0, 'O valor mínimo é 0'),
  variedadeVisual: z.number().min(0, 'O valor mínimo é 0'),
  criatividade: z.number().min(0, 'O valor mínimo é 0'),
  semEmendas: z.boolean().default(true),
})

export function GenerateImageStep() {
  const toast = useToast()
  const fileInputRef = useRef(null)
  const [previews, setPreviews] = useState([])
  const { formData, updateFormData } = usePatternContext()
  //pega o id da url /:taskId

  const { data: products = [] } = useQuery('products', fetchProducts)

  const navigate = useNavigate()

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm({
    defaultValues: {
      produto: formData.produto || '',
      prompt: formData.prompt || '',
      categoria: formData.categoria || '',
      tema: formData.tema || '',
      estiloArtistico: formData.estiloArtistico || 0,
      variedadeVisual: formData.variedadeVisual || 75,
      criatividade: formData.criatividade || 80,
      semEmendas: formData.semEmendas !== undefined ? formData.semEmendas : true,
    },
    resolver: zodResolver(formSchema),
    mode: 'onBlur',
  })

  useEffect(() => {
    if (formData.files && formData.files.length > 0) {
      const savedPreviews = formData.files.map((file) => ({
        name: file.name,
        url: URL.createObjectURL(file),
        file,
      }))
      setPreviews(savedPreviews)
    }
  }, [formData.files])

  const { mutate: generateImage, isLoading } = useGenerateImage()

  const handleFileChange = (event) => {
    const files = Array.from(event.target.files)
    if (!files.length) return

    if (files.length > 3) {
      toast({
        title: 'Limite excedido',
        description: 'Você pode selecionar no máximo 3 imagens',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      })
      files.splice(3)
    }

    const newPreviews = files.map((file) => ({
      name: file.name,
      url: URL.createObjectURL(file),
      file,
    }))

    setPreviews(newPreviews)

    event.target.value = null

    if (files.length > 0) {
    }
  }

  const openFileSelector = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click()
    }
  }

  const removePreview = (index) => {
    setPreviews((prev) => {
      const newPreviews = [...prev]
      URL.revokeObjectURL(newPreviews[index].url)
      newPreviews.splice(index, 1)
      return newPreviews
    })

    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const onSubmit = (data) => {
    const formDataToSave = {
      ...data,
      files: previews.map((p) => p.file),
    }

    updateFormData(formDataToSave)

    const aspectRatio = getAspectRatio(products, data.produto)

    const requestData = {
      ...data,
      files: previews.map((p) => p.file),
      aspectRatio,
    }

    generateImage(requestData, {
      onSuccess: (response) => {
        // Verificar se a resposta contém task_id
        if (response.task_id) {
          // Atualizar o formData com o taskId
          updateFormData({
            ...formDataToSave,
            taskId: response.task_id,
          })

          toast({
            title: 'Solicitação enviada com sucesso',
            description: 'Suas imagens estão sendo geradas. Você será redirecionado para a página de acompanhamento.',
            status: 'success',
            duration: 3000,
            isClosable: true,
          })

          navigate(`/pattern/generate-images/${response.task_id}`, {
            replace: true,
          })
        } else if (response.images) {
          updateFormData({
            generatedImages: response.images || [],
          })

          toast({
            title: 'Imagem gerada com sucesso',
            status: 'success',
            duration: 3000,
            isClosable: true,
          })

          navigate(`/pattern/generate-images/${response.task_id}`, {
            replace: true,
          })
        }
      },
      onError: (error) => {
        console.error('Erro ao gerar imagem:', error)
        toast({
          title: 'Erro ao gerar imagem',
          description: error?.response?.data?.message || 'Ocorreu um erro inesperado',
          status: 'error',
          duration: 3000,
          isClosable: true,
        })
      },
    })
  }

  return (
    <Box borderWidth="1px" borderRadius="lg" p={6} bg="white" borderColor="#378BBF" pb="80px">
      <form onSubmit={handleSubmit(onSubmit)}>
        <Text fontSize="xl" fontWeight="bold" mb={4}>
          Nova Arte
        </Text>
        <SimpleGrid columns={{ base: 2, md: 2 }} spacing={6}>
          <Flex gap={4} mb={4}>
            <FormControl isInvalid={!!errors.produto} flex="1">
              <FormLabel htmlFor="produto">Produto</FormLabel>
              <Select
                id="produto"
                placeholder="Selecione o produto"
                {...register('produto')}
                borderColor={errors.produto ? 'red.500' : '#378BBF'}
                _focus={{ borderColor: '#378BBF' }}
              >
                {products
                  .filter((p) => p.value !== 'todos')
                  .map((product) => (
                    <option key={product.id} value={product.value}>
                      {product.label}
                    </option>
                  ))}
              </Select>
              <FormErrorMessage>{errors.produto?.message}</FormErrorMessage>
            </FormControl>
          </Flex>

          <Flex alignItems="start" flexDir="column" gap={2.5} mb={4}>
            <Text fontSize="sm">Referência de imagens (Opcional, máx. 3)</Text>

            <Box
              borderWidth="1px"
              borderRadius="md"
              borderStyle="dashed"
              p={2}
              cursor="pointer"
              borderColor="#378BBF"
              onClick={openFileSelector}
              flex="1"
              maxW="300px"
              w="100%"
            >
              <InputGroup>
                <Input
                  type="file"
                  height="auto"
                  padding="10px"
                  opacity="0"
                  position="absolute"
                  multiple
                  accept="image/*"
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  style={{ display: 'none' }}
                />
                <Flex justify="space-between" alignItems="center" width="100%">
                  <Text>Escolher Arquivos</Text>
                  <FiUpload color="#378BBF" />
                </Flex>
              </InputGroup>
            </Box>

            {previews.length > 0 && (
              <SimpleGrid columns={Math.min(previews.length, 3)} spacing={2} mb={4}>
                {previews.map((preview, index) => (
                  <Box key={index} position="relative" width="100px">
                    <IconButton
                      size="xs"
                      icon={<FiX />}
                      position="absolute"
                      top="-8px"
                      right="-8px"
                      borderRadius="full"
                      colorScheme="red"
                      onClick={() => removePreview(index)}
                      zIndex={1}
                    />
                    <Image src={preview.url} alt={preview.name} borderRadius="md" boxSize="80px" objectFit="cover" />
                    <Text fontSize="xs" noOfLines={1} mt={1}>
                      {preview.name}
                    </Text>
                  </Box>
                ))}
              </SimpleGrid>
            )}
          </Flex>

          {/* Preview das imagens */}
        </SimpleGrid>

        <FormControl mb={4} isInvalid={!!errors.prompt}>
          <FormLabel htmlFor="prompt">Prompt</FormLabel>
          <Textarea
            id="prompt"
            placeholder="Descreva o que deseja gerar..."
            {...register('prompt', {
              required: 'Este campo é obrigatório',
              minLength: { value: 10, message: 'Descrição muito curta (mín. 10 caracteres)' },
            })}
            resize="vertical"
            minHeight="100px"
            borderColor={errors.prompt ? 'red.500' : '#378BBF'}
            _focus={{ borderColor: '#378BBF' }}
          />
          <FormErrorMessage>{errors.prompt?.message}</FormErrorMessage>
        </FormControl>

        <SimpleGrid coalumns={{ base: 2, md: 2 }} spacing={5}>
          <Flex justify="flex-end">
            <FormControl display="flex" alignItems="center" width="auto">
              <FormLabel htmlFor="semEmendas" mb="0" mr={2}>
                Arte sem Emendas
              </FormLabel>
              <Controller
                name="semEmendas"
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <Switch
                    id="semEmendas"
                    isChecked={field.value}
                    onChange={field.onChange}
                    colorScheme="blue"
                    sx={{
                      '& .chakra-switch__track[data-checked]': {
                        backgroundColor: '#378BBF',
                      },
                    }}
                  />
                )}
              />
            </FormControl>
          </Flex>

          <Flex gap={4}>
            <FormControl flex="1" isInvalid={!!errors.categoria}>
              <FormLabel htmlFor="categoria">Categoria</FormLabel>
              <Select
                id="categoria"
                placeholder="Escolha a categoria"
                {...register('categoria', { required: 'Selecione uma categoria' })}
                borderColor={errors.categoria ? 'red.500' : '#378BBF'}
                _focus={{ borderColor: '#378BBF' }}
              >
                <option value="abstract">Abstrato</option>
                <option value="floral">Floral</option>
                <option value="geometric">Geométrico</option>
                <option value="urban">Urbano</option>
              </Select>
              <FormErrorMessage>{errors.categoria?.message}</FormErrorMessage>
            </FormControl>

            <FormControl flex="1" isInvalid={!!errors.tema}>
              <FormLabel htmlFor="tema">Tema da Linha</FormLabel>
              <Input
                id="tema"
                placeholder="Nome da coleção que será gerada."
                {...register('tema', { required: 'Informe o tema da linha' })}
                borderColor={errors.tema ? 'red.500' : '#378BBF'}
                _focus={{ borderColor: '#378BBF' }}
              />
              <FormErrorMessage>{errors.tema?.message}</FormErrorMessage>
            </FormControl>
          </Flex>

          <SimpleGrid columns={{ base: 2, md: 2 }} spacing={4}>
            <Controller
              name="estiloArtistico"
              control={control}
              rules={{ required: true, min: 0 }}
              render={({ field }) => (
                <SliderWithTooltip
                  id="estiloArtistico"
                  label="Estilo Artístico"
                  description="Mais controle ou mais liberdade criativa para a arte."
                  value={field.value}
                  onChange={field.onChange}
                  colorScheme="#378BBF"
                  isRequired={true}
                />
              )}
            />

            <Controller
              name="variedadeVisual"
              control={control}
              rules={{ required: true, min: 0 }}
              render={({ field }) => (
                <SliderWithTooltip
                  id="variedadeVisual"
                  label="Variedade Visual"
                  description="Gera imagens mais diferentes entre si a cada prompt."
                  value={field.value}
                  onChange={field.onChange}
                  colorScheme="#378BBF"
                  isRequired={true}
                />
              )}
            />

            <Controller
              name="criatividade"
              control={control}
              rules={{ required: true, min: 0 }}
              render={({ field }) => (
                <SliderWithTooltip
                  id="criatividade"
                  label="Criatividade"
                  description="Adiciona elementos incomuns e experimentais à imagem."
                  value={field.value}
                  onChange={field.onChange}
                  colorScheme="#378BBF"
                  isRequired={true}
                />
              )}
            />
          </SimpleGrid>
        </SimpleGrid>

        <ActionButton type="submit" isLoading={isLoading} loadingText={isLoading ? 'Enviando solicitação...' : 'Gerando...'} position="fixed">
          Gerar
        </ActionButton>
      </form>
    </Box>
  )
}
